import type { AfterViewInit, ElementRef, OnInit } from '@angular/core';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    signal,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import type { ResizeEvent } from 'angular-resizable-element';
import { ResizableModule } from 'angular-resizable-element';

import type { WebLegacyWidgetEntity } from '@web/types';
import type { ImageEntity } from '@sites/entity-core';
import type { ImageForm, ImageZeroForm } from '@sites/entity-signal-form';

import { environment } from '@web-env/environment';
import {
    formatFileUrl,
    formatFileUrlWithResize,
    isResizeDisabledForPage,
} from '@components/sp-file-manager/src/helpers';

import { BLANK_IMAGE } from '@web/widgets/image/constants/image.consts';
import { EResizeType } from '@web/widgets/image/type/image-widget.type';
import { WebBaseViewSignalFormComponent } from '@web/widgets/common/abstract/web-base-view-signal-form.component';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { RouterFacade } from '@web/store/router/router.facade';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';
import { SppFallbackImgDirective } from '@common/directives/fallback-img.directive';

import {
    getImageBoxCssClasses,
    getImageBoxCssVars,
    getImageDescriptionCssClasses,
    getImageDescriptionCssVars,
    getImageElementCssClasses,
    getImageMaskValue,
    getImageRootCssClasses,
    getImageRootCssVars,
    getImageTextCssVars,
} from '@sites/entity-core/src/widget/image/styles/image.css-vars';
import { commonFontSizeCoefficient, themePStyles } from '@sites/site-theme/src/styles/text.styles';

/**
 * Editor preview for the `image` widget — fully signal-based. The form is
 * shared with the control panel via `WebLegacyWidgetSignalFormService` (ref-counted
 * by widget id). Visual styling lives in LESS + CSS variables (`image.base.less`);
 * the resize handles + focal-point drag write back into the form (one write per gesture, on end — per-frame updates only touch the DOM).
 */
@Component({
    selector: 'web-image-view',
    templateUrl: './image-view.component.html',
    styleUrls: ['./image-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ResizableModule, SppFallbackImgDirective],
})
export class ImageViewComponent
    extends WebBaseViewSignalFormComponent<WebLegacyWidgetEntity<ImageEntity['data']>>
    implements OnInit, AfterViewInit
{
    @ViewChild('resizeBox') public resizeBox: ElementRef<HTMLElement>;
    @ViewChild('imageWrapper') imageWrapper: ElementRef<HTMLDivElement>;

    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly routerFacade: RouterFacade = inject(RouterFacade);

    public form!: ImageForm;

    public readonly EResizeType = EResizeType;
    public readonly BLANK_IMAGE = BLANK_IMAGE;

    public readonly isDeviceMobile$ = this.layoutFacade.isDeviceMobile$;
    public readonly isDeviceTablet$ = this.layoutFacade.isDeviceTablet$;

    private readonly routerParams = toSignal(this.routerFacade.routerParams$);

    // --- CSS custom property maps + modifier classes (template bindings) ---
    public readonly rootVars = computed(() => getImageRootCssVars(this.form.value()));
    public readonly rootClasses = computed(() => getImageRootCssClasses(this.form.value()));
    public readonly boxVars = computed(() => getImageBoxCssVars(this.form.value()));
    public readonly boxClasses = computed(() => getImageBoxCssClasses(this.form.value(), this.isSpecific()));
    public readonly imageClasses = computed(() => getImageElementCssClasses(this.form.value()));
    public readonly maskValue = computed(() => getImageMaskValue(this.imageValue()));
    public readonly descriptionVars = computed(() => getImageDescriptionCssVars(this.form.value()));
    public readonly descriptionClasses = computed(() => getImageDescriptionCssClasses(this.form.value()));
    public readonly textVars = computed(() =>
        getImageTextCssVars(this.form.value(), themePStyles, commonFontSizeCoefficient)
    );

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<ImageForm>(this.widget().id, this.destroyRef);
    }

    public ngAfterViewInit(): void {
        const image = this.form.image().value();
        if (this.resizeTypeValue() !== EResizeType.fill || !image?.resizedHeight || image.resizedWidth) return;

        afterNextRender(
            () => {
                this.form.transactWithoutHistory(() => {
                    this.form.updateImage({ resizedWidth: this.imageWrapper.nativeElement.clientWidth });
                });
            },
            { injector: this.injector }
        );
    }

    // --- Derived values (template bindings) ---

    public readonly isZeroWidget = computed(() => this.form?.value().type === 'zero');

    public readonly imageValue = computed(() => this.form?.image().value() ?? null);

    public readonly resizeTypeValue = computed(() => this.form?.resizeType().value() as EResizeType);

    /** Mirrors the published page: page 704745 is served CDN originals only. */
    public readonly isResizeDisabled = computed(() => isResizeDisabledForPage(this.routerParams()?.pageId));

    public readonly srcSets = computed(() => (this.isResizeDisabled() ? [] : (this.imageValue()?.srcSets ?? [])));

    public readonly imgSrc = computed<string>(() => {
        const image = this.imageValue();
        if (!image) {
            return BLANK_IMAGE;
        }

        if (this.isResizeDisabled()) {
            return formatFileUrl(image, environment.fileManagerCdnUrl);
        }

        return formatFileUrlWithResize(image, environment.fileManagerCdnUrl, 720);
    });

    public readonly imgFallbackSrc = computed<string>(() => {
        const image = this.imageValue();
        if (!image) {
            return BLANK_IMAGE;
        }

        return formatFileUrl(image, environment.fileManagerCdnUrl);
    });

    public readonly overlayImageSrc = computed<string | boolean>(() => {
        const image = this.imageValue();
        if (!image) {
            return false;
        }

        if (!image.overlayImage || !image.isImageEffect) {
            return false;
        }

        if (this.isResizeDisabled()) {
            return formatFileUrl(image, environment.fileManagerCdnUrl);
        }

        return formatFileUrlWithResize(image, environment.fileManagerCdnUrl, 720);
    });

    public readonly objectPositionStyle = computed<string>(() => {
        const image = this.imageValue();
        if (!image) {
            return;
        }

        if (this.isZeroWidget()) {
            return (this.form as ImageZeroForm).imagePosition?.().value();
        }

        const drag = this.dragPosition();
        if (drag) {
            return `${drag.x}% ${drag.y}%`;
        }

        return (image.backgroundPositionX || 50) + '% ' + (image.backgroundPositionY || 50) + '%';
    });

    // --- Resize handles (non-zero placement only) ---

    public onResizeStart(event: ResizeEvent, target: HTMLElement): void {}

    public onResizing(event: ResizeEvent, target: HTMLElement): void {
        if (typeof event.edges.bottom !== 'number') return;
        if (event.rectangle.width <= 50) return;
        if (this.resizeTypeValue() === EResizeType.scale) {
            if (event.rectangle.width < this.imageValue()?.imageWidth) {
                target.style.maxWidth = event.rectangle.width + 'px';
            }
        } else if (this.resizeTypeValue() === EResizeType.fill) {
            const newPadding = (event.rectangle.height / event.rectangle.width) * 100;
            target.style.paddingTop = newPadding + '%';
        }
    }

    public onResizeEnd(event: ResizeEvent, target: HTMLElement): void {
        if (this.resizeTypeValue() === EResizeType.scale) {
            if (event.rectangle.width >= this.imageValue()?.imageWidth) {
                this.form.setMaxWidth(this.imageValue()?.imageWidth);
            } else {
                this.form.setMaxWidth(event.rectangle.width);
            }
        } else if (this.resizeTypeValue() === EResizeType.fill) {
            this.form.updateImage({
                resizedHeight: event.rectangle.height,
                resizedWidth: event.rectangle.width,
            });
        }
        target.style.maxWidth = '';
        target.style.paddingTop = '';
    }

    // --- Hover cross-fade to the overlay image ---

    private readonly isOverlayHovered = signal(false);

    /** Guarded by `overlayImageSrc` so removing the overlay mid-hover cannot leave the image at 0. */
    public readonly mainImageOpacity = computed(() => (this.isOverlayHovered() && this.overlayImageSrc() ? 0 : 1));
    public readonly overlayImageOpacity = computed(() => (this.isOverlayHovered() ? 1 : 0));

    public mouseImgIn(): void {
        this.isOverlayHovered.set(true);
    }

    public mouseImgOut(): void {
        this.isOverlayHovered.set(false);
    }

    // --- Focal-point drag (non-zero placement only) ---

    /** Live gesture position; `null` between drags, when the committed form value takes over. */
    private readonly dragPosition = signal<{ x: number; y: number } | null>(null);

    private cursorPosBefore = { x: 0, y: 0 };
    private imagePosBefore: { x: number; y: number } | null = null;
    private isMouseDown = false;

    public mouseUp(): void {
        if (!this.isMouseDown || this.isZeroWidget()) {
            return;
        }

        this.isMouseDown = false;

        const position = this.dragPosition();
        this.dragPosition.set(null);

        if (!position) {
            return;
        }

        this.form.updateImage({
            backgroundPositionX: position.x,
            backgroundPositionY: position.y,
        });
    }

    public mouseDown(event: MouseEvent, image: ImageEntity['data']['image']): void {
        event.preventDefault();

        if (this.isZeroWidget() || !image) {
            return;
        }

        this.isMouseDown = true;
        this.dragPosition.set(null);
        this.imagePosBefore = {
            x: image.backgroundPositionX || 50,
            y: image.backgroundPositionY || 50,
        };
        this.cursorPosBefore = { x: event.pageX, y: event.pageY };
    }

    public move(event: MouseEvent, imageRef: HTMLImageElement): void {
        event.preventDefault();

        if (this.isZeroWidget()) {
            return;
        }

        if (event.buttons !== 1 || !this.imagePosBefore) {
            return;
        }

        const newXPos = this.imagePosBefore.x + ((this.cursorPosBefore.x - event.clientX) / imageRef.width) * 100;
        const newYPos = this.imagePosBefore.y + ((this.cursorPosBefore.y - event.clientY) / imageRef.height) * 100;

        if (newXPos < 0 || newXPos > 100 || newYPos < 0 || newYPos > 100) {
            return;
        }

        this.dragPosition.set({ x: newXPos, y: newYPos });
    }
}
